import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

export default function DriverAttendanceTest() {
    const API_URL = process.env.REACT_APP_API_URL;

    // Data
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [todayRecords, setTodayRecords] = useState([]);

    // Form state
    const [selectedDriver, setSelectedDriver] = useState('');
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [currentOdometer, setCurrentOdometer] = useState('');
    const [lastKm, setLastKm] = useState(null);
    const [lastKmRaw, setLastKmRaw] = useState(0);  // numeric for validation
    const [kmWarning, setKmWarning] = useState('');
    const [kmError, setKmError] = useState('');

    // Session state
    const [checkedIn, setCheckedIn] = useState(false);
    const [checkInTime, setCheckInTime] = useState(null);
    const [elapsed, setElapsed] = useState('00:00:00');
    const [activeRecord, setActiveRecord] = useState(null);

    // UI
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    const [failedSyncs, setFailedSyncs] = useState([]);
    const [retryingId, setRetryingId] = useState(null);

    const timerRef = useRef(null);

    useEffect(() => {
        loadData();
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    // Running clock
    useEffect(() => {
        if (checkedIn && checkInTime) {
            timerRef.current = setInterval(() => {
                const now = new Date();
                const diff = Math.floor((now - checkInTime) / 1000);
                const h = String(Math.floor(diff / 3600)).padStart(2, '0');
                const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
                const s = String(diff % 60).padStart(2, '0');
                setElapsed(`${h}:${m}:${s}`);
            }, 1000);
        } else if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [checkedIn, checkInTime]);

    const loadData = async () => {
        setLoadingData(true);
        await Promise.all([fetchDrivers(), fetchVehicles(), fetchTodayRecords(), fetchFailedSyncs()]);
        setLoadingData(false);
    };

    const fetchDrivers = async () => {
        try {
            // Load TMS drivers (required for valid driverId in attendance DB)
            const token = sessionStorage.getItem('token');
            const res = await axios.get(`${API_URL}shypvdriverapp/driver/getAllDrivers`, { headers: { Authorization: `Bearer ${token}` } });
            const data = res.data?.data || res.data?.responseObject || [];
            if (Array.isArray(data) && data.length > 0) {
                setDrivers(data.map(d => ({ id: d.driverId, name: d.name || d.userName || `Driver ${d.driverId}`, phone: d.phoneNumber || '' })));
                return;
            }
        } catch (err) {
            console.error('TMS drivers failed, trying FleetP:', err);
        }
        // Fallback to FleetP drivers
        try {
            const res = await axios.get(`${API_URL}shypvdriverapp/api/integrations/default/FLEETP/drivers`);
            const data = res.data?.data || [];
            if (Array.isArray(data) && data.length > 0) {
                setDrivers(data.map(d => ({ id: d.id, name: d.name || `Driver ${d.id}`, phone: d.phone || '' })));
            }
        } catch (err) { console.error('FleetP drivers also failed:', err); }
    };

    const fetchVehicles = async () => {
        try {
            const res = await axios.get(`${API_URL}shypvdriverapp/api/integrations/default/FLEETP/vehicles`);
            const data = res.data?.data || [];
            if (Array.isArray(data) && data.length > 0) {
                setVehicles(data.map(v => ({ id: v.id, plate: v.plate_number || v.id, model: v.model || '', latest_km: v.latest_km || '0', latest_km_date: v.latest_km_date || '' })));
                return;
            }
        } catch (err) {
            console.error('FleetP vehicles failed, trying local:', err);
        }
        // Fallback
        try {
            const token = sessionStorage.getItem('token');
            const res = await axios.get(`${API_URL}shypvdriverapp/vehicle/getVehicles`, { headers: { Authorization: `Bearer ${token}` } });
            const data = res.data?.data || res.data?.responseObject || [];
            if (Array.isArray(data)) {
                setVehicles(data.map(v => ({ id: v.vehicle_code, plate: v.vehicle_registration_number || v.vehicle_name || `V-${v.vehicle_code}`, model: v.vehicle_name || '' })));
            }
        } catch (err) { console.error('Local vehicles also failed:', err); }
    };

    const fetchTodayRecords = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const today = new Date().toISOString().split('T')[0];
            const res = await axios.get(
                `${API_URL}shypvdriverapp/driver-attendance/all?dateFrom=${today}&dateTo=${today}&size=50`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const pageData = res.data?.data || res.data?.responseObject;
            const data = pageData?.content || pageData || [];
            if (Array.isArray(data)) {
                setTodayRecords(data.map(r => {
                    const driverInfo = r.driver || {};
                    return {
                        id: r.id,
                        driverId: driverInfo.id || r.driverId,
                        driverName: driverInfo.name || r.driverName || `Driver ${driverInfo.id || r.driverId}`,
                        checkIn: r.checkInTime,
                        checkOut: r.checkOutTime,
                        vehicle: r.vehiclePlateNumber || '-',
                        km: r.kmReading,
                        status: r.checkOutTime ? 'CHECKED_OUT' : (r.status || 'CHECKED_IN'),
                    };
                }));
            }
        } catch (err) { console.error('Failed to fetch records:', err); }
    };

    const handleVehicleSelect = (vehicleId) => {
        setSelectedVehicle(vehicleId);
        setCurrentOdometer('');
        setKmWarning('');
        setKmError('');
        const vehicle = vehicles.find(v => v.id === vehicleId);
        if (vehicle) {
            const rawKm = parseInt(vehicle.latest_km, 10) || 0;
            setLastKmRaw(rawKm);
            setLastKm(rawKm > 0
                ? `${rawKm} km` + (vehicle.latest_km_date ? ` (${vehicle.latest_km_date})` : '')
                : 'No reading yet');
        } else {
            setLastKm(null);
            setLastKmRaw(0);
        }
    };

    const handleOdometerChange = (value) => {
        // Only allow digits (no decimals, no negatives)
        const cleaned = value.replace(/[^0-9]/g, '');
        setCurrentOdometer(cleaned);
        setKmWarning('');
        setKmError('');

        if (!cleaned) return;

        const km = parseInt(cleaned, 10);
        if (lastKmRaw > 0) {
            if (km <= lastKmRaw) {
                setKmError(`Must be greater than last reading (${lastKmRaw} km). FleetP will reject this.`);
            } else {
                const diff = km - lastKmRaw;
                if (diff > 1000) {
                    setKmWarning(`Large jump: +${diff} km from last reading (${lastKmRaw}). Please verify.`);
                }
            }
        }
    };

    const handleCheckIn = async () => {
        if (!selectedDriver) { setError('Please select a driver'); return; }
        if (!selectedVehicle) { setError('Please select a vehicle'); return; }
        if (!currentOdometer) { setError('Please enter current odometer reading'); return; }

        const km = parseInt(currentOdometer, 10);
        if (isNaN(km) || km <= 0) { setError('Please enter a valid odometer reading'); return; }

        // Block if KM is not greater than last FleetP reading
        if (lastKmRaw > 0 && km <= lastKmRaw) {
            setError(`Odometer reading must be greater than last FleetP reading (${lastKmRaw} km). Enter a value above ${lastKmRaw}.`);
            return;
        }

        setLoading(true);
        setError('');
        setMsg('');

        const now = new Date();
        const checkInDateTime = `${now.toISOString().split('T')[0]}T${now.toTimeString().split(' ')[0]}`;

        try {
            const token = sessionStorage.getItem('token');
            const selectedVeh = vehicles.find(v => v.id === selectedVehicle);

            const payload = {
                driverId: parseInt(selectedDriver),
                vehiclePlateNumber: selectedVeh?.plate || selectedVehicle,
                kmReading: km,
                checkInDateTime: checkInDateTime,
                fleetpVehicleId: selectedVeh?.id || selectedVehicle,
                fleetpDriverId: selectedDriver,
            };

            const res = await axios.post(
                `${API_URL}shypvdriverapp/driver-attendance/manual`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const savedId = res.data?.data?.id || res.data?.responseObject?.id || null;

            setCheckInTime(now);
            setCheckedIn(true);
            setActiveRecord({
                id: savedId,
                driverId: selectedDriver,
                driverName: drivers.find(d => String(d.id) === String(selectedDriver))?.name || selectedDriver,
                vehicle: selectedVeh?.plate || selectedVehicle,
                vehicleId: selectedVeh?.id || selectedVehicle,
                checkInTime: now,
                km: km,
            });

            setMsg(`Check-in recorded at ${now.toLocaleTimeString()}`);
            fetchTodayRecords();
            fetchFailedSyncs();
        } catch (err) {
            console.error('Check-in failed:', err);
            setError(err.response?.data?.message || 'Check-in failed. Please try again.');
        }
        setLoading(false);
    };

    const handleCheckOut = async () => {
        if (!activeRecord) { setError('No active check-in found'); return; }

        setLoading(true);
        setError('');
        setMsg('');

        const now = new Date();
        const checkOutDateTime = `${now.toISOString().split('T')[0]}T${now.toTimeString().split(' ')[0]}`;

        try {
            const token = sessionStorage.getItem('token');
            const payload = {
                driverId: parseInt(activeRecord.driverId),
                vehiclePlateNumber: activeRecord.vehicle,
                checkOutDateTime: checkOutDateTime,
            };

            await axios.post(
                `${API_URL}shypvdriverapp/driver-attendance/manual`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setMsg(`Check-out recorded at ${now.toLocaleTimeString()} | Duration: ${elapsed}`);
            setCheckedIn(false);
            setCheckInTime(null);
            setActiveRecord(null);
            setSelectedDriver('');
            setSelectedVehicle('');
            setCurrentOdometer('');
            setLastKm(null);
            setLastKmRaw(0);
            setKmWarning('');
            setKmError('');
            setElapsed('00:00:00');
            fetchTodayRecords();
            fetchFailedSyncs();
        } catch (err) {
            console.error('Check-out failed:', err);
            setError(err.response?.data?.message || 'Check-out failed. Please try again.');
        }
        setLoading(false);
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const getDuration = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return '-';
        const diff = Math.floor((new Date(checkOut) - new Date(checkIn)) / 1000);
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        return `${h}h ${m}m`;
    };

    const fetchFailedSyncs = async () => {
        try {
            const res = await axios.get(`${API_URL}shypvdriverapp/api/integrations/default/FLEETP/failed-syncs`);
            setFailedSyncs(res.data?.data || []);
        } catch (err) { setFailedSyncs([]); }
    };

    const handleRetrySync = async (syncLogId) => {
        setRetryingId(syncLogId);
        setError('');
        try {
            const res = await axios.post(`${API_URL}shypvdriverapp/api/integrations/sync-logs/${syncLogId}/retry`);
            if (res.data?.success) {
                setMsg(`Sync retry successful: ${res.data.message}`);
                fetchFailedSyncs();
                fetchTodayRecords();
            } else {
                setError(`Retry failed: ${res.data?.message || 'Unknown error'}`);
            }
        } catch (err) {
            setError(`Retry failed: ${err.response?.data?.message || err.message}`);
        }
        setRetryingId(null);
    };

    const handleRetryAll = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.post(`${API_URL}shypvdriverapp/api/integrations/default/FLEETP/retry-all`);
            setMsg(`Retry complete: ${res.data?.retrySuccess || 0} succeeded, ${res.data?.retryFailed || 0} failed`);
            fetchFailedSyncs();
        } catch (err) {
            setError(`Retry all failed: ${err.response?.data?.message || err.message}`);
        }
        setLoading(false);
    };

    if (loadingData) {
        return (
            <div style={s.container}>
                <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px', marginRight: '10px' }}></i>
                    Loading drivers & vehicles from FleetP...
                </div>
            </div>
        );
    }

    return (
        <div style={s.container}>
            {/* Header */}
            <div style={s.header}>
                <h2 style={s.title}>
                    <i className="fa fa-id-card-o" style={{ marginRight: '10px', color: '#1976d2' }}></i>
                    Driver Attendance
                </h2>
                <p style={s.subtitle}>Check-in with vehicle & odometer → Running clock → Check-out</p>
                <div style={s.statRow}>
                    <div style={s.stat}><strong>{drivers.length}</strong> <span>Drivers</span></div>
                    <div style={s.stat}><strong>{vehicles.length}</strong> <span>Vehicles</span></div>
                    <div style={s.stat}><strong>{todayRecords.length}</strong> <span>Today</span></div>
                </div>
            </div>

            {msg && <div style={s.success}><i className="fa fa-check-circle" style={{ marginRight: '8px' }}></i>{msg}</div>}
            {error && <div style={s.error}><i className="fa fa-exclamation-circle" style={{ marginRight: '8px' }}></i>{error}</div>}

            <div style={s.mainGrid}>
                {/* LEFT: Attendance Form */}
                <div style={s.card}>
                    {!checkedIn ? (
                        <>
                            <h3 style={s.cardTitle}>
                                <i className="fa fa-sign-in" style={{ marginRight: '8px', color: '#22c55e' }}></i>
                                Check In
                            </h3>

                            {/* Driver Selection */}
                            <div style={s.field}>
                                <label style={s.label}>
                                    <i className="fa fa-user" style={{ marginRight: '6px' }}></i>Driver *
                                </label>
                                <select value={selectedDriver} onChange={e => setSelectedDriver(e.target.value)} style={s.select}>
                                    <option value="">Select Driver ({drivers.length} available)</option>
                                    {drivers.map(d => (
                                        <option key={d.id} value={d.id}>{d.name} {d.phone ? `(${d.phone})` : ''}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Vehicle Selection */}
                            <div style={s.field}>
                                <label style={s.label}>
                                    <i className="fa fa-car" style={{ marginRight: '6px' }}></i>Vehicle *
                                </label>
                                <select value={selectedVehicle} onChange={e => handleVehicleSelect(e.target.value)} style={s.select}>
                                    <option value="">Select Vehicle ({vehicles.length} available)</option>
                                    {vehicles.map(v => (
                                        <option key={v.id} value={v.id}>{v.plate} {v.model ? `- ${v.model}` : ''} {v.latest_km && v.latest_km !== '0' ? `[${v.latest_km} km]` : ''}</option>
                                    ))}
                                </select>
                                {lastKm !== null && (
                                    <div style={s.lastKmBadge}>
                                        <i className="fa fa-tachometer" style={{ marginRight: '6px' }}></i>
                                        Last KM: <strong>{lastKm}</strong>
                                    </div>
                                )}
                            </div>

                            {/* Odometer */}
                            <div style={s.field}>
                                <label style={s.label}>
                                    <i className="fa fa-tachometer" style={{ marginRight: '6px' }}></i>Current Odometer Reading *
                                </label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={currentOdometer}
                                    onChange={e => handleOdometerChange(e.target.value)}
                                    placeholder={lastKmRaw > 0 ? `Must be above ${lastKmRaw} (e.g. ${lastKmRaw + 50})` : 'Enter current odometer (e.g. 45230)'}
                                    style={{
                                        ...s.input,
                                        borderColor: kmError ? '#dc2626' : kmWarning ? '#f59e0b' : '#ddd',
                                        backgroundColor: kmError ? '#fef2f2' : kmWarning ? '#fffbeb' : '#fff',
                                    }}
                                />
                                {lastKmRaw > 0 && !kmError && !kmWarning && (
                                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                                        Minimum: <strong>{lastKmRaw + 1}</strong> km (last: {lastKmRaw})
                                    </div>
                                )}
                                {kmError && (
                                    <div style={{ marginTop: '4px', padding: '6px 10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px', color: '#dc2626', display: 'flex', alignItems: 'center' }}>
                                        <i className="fa fa-times-circle" style={{ marginRight: '6px' }}></i>{kmError}
                                    </div>
                                )}
                                {kmWarning && (
                                    <div style={{ marginTop: '4px', padding: '6px 10px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '12px', color: '#92400e', display: 'flex', alignItems: 'center' }}>
                                        <i className="fa fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>{kmWarning}
                                    </div>
                                )}
                            </div>

                            {/* Check-in Time Preview */}
                            <div style={s.timePreview}>
                                <i className="fa fa-clock-o" style={{ marginRight: '8px', color: '#1976d2' }}></i>
                                Check-in time will be: <strong>{new Date().toLocaleTimeString()}</strong>
                                <span style={{ color: '#999', marginLeft: '8px', fontSize: '12px' }}>(auto-captured)</span>
                            </div>

                            <button onClick={handleCheckIn} disabled={loading || !!kmError} style={{
                                ...s.checkInBtn,
                                opacity: (loading || !!kmError) ? 0.5 : 1,
                                cursor: (loading || !!kmError) ? 'not-allowed' : 'pointer',
                            }}>
                                {loading ? (
                                    <><i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Saving...</>
                                ) : (
                                    <><i className="fa fa-sign-in" style={{ marginRight: '8px' }}></i>CHECK IN</>
                                )}
                            </button>
                        </>
                    ) : (
                        <>
                            {/* Checked In - Running Clock View */}
                            <div style={s.activeSession}>
                                <div style={s.sessionHeader}>
                                    <div style={s.liveBadge}>
                                        <div style={s.liveDot}></div>
                                        <span>ACTIVE</span>
                                    </div>
                                    <h3 style={{ margin: 0, fontSize: '18px', color: '#1a1a2e' }}>Shift In Progress</h3>
                                </div>

                                {/* Clock */}
                                <div style={s.clockContainer}>
                                    <div style={s.clock}>{elapsed}</div>
                                    <div style={s.clockLabel}>Duration</div>
                                </div>

                                {/* Session Details */}
                                <div style={s.sessionDetails}>
                                    <div style={s.sessionRow}>
                                        <i className="fa fa-user" style={{ width: '20px', color: '#666' }}></i>
                                        <span style={{ color: '#333', fontWeight: '500' }}>{activeRecord?.driverName}</span>
                                    </div>
                                    <div style={s.sessionRow}>
                                        <i className="fa fa-car" style={{ width: '20px', color: '#666' }}></i>
                                        <span style={{ color: '#333' }}>{activeRecord?.vehicle}</span>
                                    </div>
                                    <div style={s.sessionRow}>
                                        <i className="fa fa-clock-o" style={{ width: '20px', color: '#666' }}></i>
                                        <span style={{ color: '#333' }}>In: {activeRecord?.checkInTime?.toLocaleTimeString()}</span>
                                    </div>
                                    <div style={s.sessionRow}>
                                        <i className="fa fa-tachometer" style={{ width: '20px', color: '#666' }}></i>
                                        <span style={{ color: '#333' }}>Odometer: {activeRecord?.km} KM</span>
                                    </div>
                                </div>

                                {/* Check-out Time Preview */}
                                <div style={{ ...s.timePreview, backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
                                    <i className="fa fa-clock-o" style={{ marginRight: '8px', color: '#dc3545' }}></i>
                                    Check-out time will be: <strong>{new Date().toLocaleTimeString()}</strong>
                                    <span style={{ color: '#999', marginLeft: '8px', fontSize: '12px' }}>(auto-captured)</span>
                                </div>

                                <button onClick={handleCheckOut} disabled={loading} style={s.checkOutBtn}>
                                    {loading ? (
                                        <><i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Saving...</>
                                    ) : (
                                        <><i className="fa fa-sign-out" style={{ marginRight: '8px' }}></i>CHECK OUT</>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* RIGHT: Today's Records */}
                <div style={s.card}>
                    <h3 style={s.cardTitle}>
                        <i className="fa fa-list" style={{ marginRight: '8px', color: '#1976d2' }}></i>
                        Today's Records ({todayRecords.length})
                    </h3>

                    {todayRecords.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                            <i className="fa fa-inbox" style={{ fontSize: '32px', marginBottom: '12px', display: 'block' }}></i>
                            No attendance records for today
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Driver</th>
                                        <th style={s.th}>Vehicle</th>
                                        <th style={s.th}>In</th>
                                        <th style={s.th}>Out</th>
                                        <th style={s.th}>Duration</th>
                                        <th style={s.th}>KM</th>
                                        <th style={s.th}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {todayRecords.map((r, i) => (
                                        <tr key={r.id || i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                            <td style={s.td}>{r.driverName}</td>
                                            <td style={s.td}>{r.vehicle}</td>
                                            <td style={s.td}>{formatTime(r.checkIn)}</td>
                                            <td style={s.td}>{formatTime(r.checkOut)}</td>
                                            <td style={s.td}>{getDuration(r.checkIn, r.checkOut)}</td>
                                            <td style={s.td}>{r.km || '-'}</td>
                                            <td style={s.td}>
                                                <span style={{
                                                    padding: '2px 8px',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    backgroundColor: r.status === 'CHECKED_IN' ? '#dcfce7' : '#e0f2fe',
                                                    color: r.status === 'CHECKED_IN' ? '#166534' : '#0369a1',
                                                }}>
                                                    {r.status === 'CHECKED_IN' ? '● Active' : '✓ Done'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Sync Status */}
                    <div style={s.syncFooter}>
                        <i className="fa fa-cloud-upload" style={{ marginRight: '6px', color: '#22c55e' }}></i>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                            Records auto-synced to FleetP via backend
                        </span>
                    </div>

                    {/* Failed Syncs */}
                    {failedSyncs.length > 0 && (
                        <div style={{ marginTop: '16px', padding: '14px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h4 style={{ margin: 0, fontSize: '14px', color: '#dc2626' }}>
                                    <i className="fa fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                                    Failed Syncs ({failedSyncs.length})
                                </h4>
                                <button onClick={handleRetryAll} disabled={loading} style={{
                                    padding: '4px 12px', fontSize: '12px', fontWeight: '600',
                                    backgroundColor: '#dc2626', color: '#fff', border: 'none',
                                    borderRadius: '6px', cursor: 'pointer'
                                }}>
                                    {loading ? 'Retrying...' : 'Retry All'}
                                </button>
                            </div>
                            {failedSyncs.map(f => (
                                <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid #fecaca', fontSize: '12px' }}>
                                    <div>
                                        <strong>{f.syncType}</strong> - Ref#{f.referenceId}
                                        <div style={{ color: '#991b1b', fontSize: '11px' }}>{f.errorMessage}</div>
                                        <div style={{ color: '#999', fontSize: '10px' }}>Retries: {f.retryCount}</div>
                                    </div>
                                    <button onClick={() => handleRetrySync(f.id)} disabled={retryingId === f.id} style={{
                                        padding: '3px 10px', fontSize: '11px',
                                        backgroundColor: '#f97316', color: '#fff', border: 'none',
                                        borderRadius: '4px', cursor: 'pointer'
                                    }}>
                                        {retryingId === f.id ? <i className="fa fa-spinner fa-spin"></i> : 'Retry'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const s = {
    container: { padding: '20px', maxWidth: '1200px', margin: '0 auto' },
    header: { marginBottom: '20px' },
    title: { fontSize: '22px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 6px 0', display: 'flex', alignItems: 'center' },
    subtitle: { fontSize: '13px', color: '#666', margin: '0 0 12px 0' },
    statRow: { display: 'flex', gap: '16px' },
    stat: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#555', backgroundColor: '#f0f9ff', padding: '4px 12px', borderRadius: '6px' },
    mainGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '24px' },
    cardTitle: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 20px 0', display: 'flex', alignItems: 'center' },
    field: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#333', marginBottom: '6px' },
    select: { width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fff' },
    input: { width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' },
    lastKmBadge: { marginTop: '8px', padding: '8px 12px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '13px', color: '#92400e' },
    timePreview: { padding: '10px 14px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '13px', color: '#333', marginBottom: '16px' },
    checkInBtn: { width: '100%', padding: '14px', fontSize: '16px', fontWeight: '600', color: '#fff', backgroundColor: '#22c55e', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    checkOutBtn: { width: '100%', padding: '14px', fontSize: '16px', fontWeight: '600', color: '#fff', backgroundColor: '#dc3545', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '16px' },
    activeSession: {},
    sessionHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
    liveBadge: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#dcfce7', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', color: '#166534' },
    liveDot: { width: '8px', height: '8px', backgroundColor: '#22c55e', borderRadius: '50%', animation: 'pulse 2s infinite' },
    clockContainer: { textAlign: 'center', padding: '24px 0' },
    clock: { fontSize: '48px', fontWeight: '700', color: '#1a1a2e', fontFamily: 'monospace', letterSpacing: '4px' },
    clockLabel: { fontSize: '13px', color: '#666', marginTop: '6px' },
    sessionDetails: { backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '16px' },
    sessionRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', fontSize: '14px' },
    success: { padding: '12px 16px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '8px', marginBottom: '16px', fontSize: '14px', display: 'flex', alignItems: 'center' },
    error: { padding: '12px 16px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '8px', marginBottom: '16px', fontSize: '14px', display: 'flex', alignItems: 'center' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: { padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', color: '#666', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase' },
    td: { padding: '10px 8px', borderBottom: '1px solid #f3f4f6', color: '#333' },
    syncFooter: { marginTop: '16px', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '6px', display: 'flex', alignItems: 'center' },
};
